import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Apple, ArrowRight, Lock } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { DEFAULT_POST_LOGIN_PATH, redirectToPostLoginTarget, readReturnUrlFromSearchParams, resolvePostLoginPath } from '../utils/postLoginRedirect';

const authStateMessages = {
  'signin-failed': {
    title: 'Kinde sign-in was not completed',
    body: 'The Kinde hosted screen returned an error or the login was cancelled. Retry the Kinde flow below.',
    tone: 'danger',
  },
  'verify-email': {
    title: 'Verify your email in Kinde',
    body: 'Complete email verification on the Kinde hosted screen, then come back and sign in again.',
    tone: 'warning',
  },
  'reset-password': {
    title: 'Password reset is required',
    body: 'Use the temporary password on the Kinde hosted screen and follow the change-password prompt there.',
    tone: 'warning',
  },
  mfa: {
    title: 'Multi-factor authentication enabled',
    body: 'After email and password, Kinde will request your MFA step automatically.',
    tone: 'info',
  },
};

export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, provider, startProviderLogin } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirectingToKinde, setIsRedirectingToKinde] = useState(false);

  const returnUrl = useMemo(
    () => resolvePostLoginPath(readReturnUrlFromSearchParams(searchParams) || DEFAULT_POST_LOGIN_PATH),
    [searchParams],
  );
  const authState = searchParams.get('authState');
  const authReason = searchParams.get('reason');
  const authStateMessage = authState ? authStateMessages[authState] : null;
  const showSocialButtons = Boolean(provider?.kinde?.showSocialButtons);
  const googleConnectionId = (provider?.kinde?.googleConnectionId || '').trim();
  const appleConnectionId = (provider?.kinde?.appleConnectionId || '').trim();
  const usernameConnectionId = (provider?.kinde?.usernameConnectionId || '').trim();
  const isContinuingSso = returnUrl.startsWith('/api/sso/');

  // SSO continue: navigate as soon as auth is known (layout phase) to avoid painting
  // the login form / waiting an extra paint+effect tick after /api/auth/me.
  React.useLayoutEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (isContinuingSso) {
      window.location.replace(returnUrl);
      return;
    }

    redirectToPostLoginTarget(returnUrl, navigate);
  }, [isAuthenticated, isContinuingSso, isLoading, navigate, returnUrl]);

  React.useEffect(() => {
    // When navigating back to this page (including BFCache restores), ensure the CTA isn't stuck disabled.
    setIsRedirectingToKinde(false);
    setIsSubmitting(false);
  }, [location.key]);

  React.useEffect(() => {
    const onPageShow = () => {
      setIsRedirectingToKinde(false);
      setIsSubmitting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const redirectUrl = await login(identifier, password, returnUrl);
      redirectToPostLoginTarget(redirectUrl, navigate);
    } catch (submissionError) {
      setError(submissionError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onContinueToKinde = () => {
    const safeIdentifier = identifier.trim();
    if (!safeIdentifier) {
      setError('Please enter your email or username first.');
      return;
    }

    const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(safeIdentifier) && safeIdentifier.length <= 254;

    setError('');
    setIsRedirectingToKinde(true);

    if (looksLikeEmail) {
      // Force a fresh interactive login so Kinde cannot silently reuse the previous SSO session.
      startProviderLogin(returnUrl, { loginHint: safeIdentifier, prompt: 'login', maxAge: 0 });
      return;
    }

    const usernameHint = safeIdentifier.startsWith('username:') ? safeIdentifier : `username:${safeIdentifier}`;
    if (!usernameConnectionId) {
      setError('Username sign-in is not configured yet. Ask an admin to set Kinde.UsernameConnectionId in appsettings.');
      setIsRedirectingToKinde(false);
      return;
    }

    // Force fresh auth so Kinde does not reuse a previous identity/session.
    startProviderLogin(returnUrl, { loginHint: usernameHint, connectionId: usernameConnectionId, prompt: 'login', maxAge: 0 });
  };

  if (!isLoading && isAuthenticated && isContinuingSso) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="portal-loading-inline">Continuing to platform…</div>
        </div>
      </div>
    );
  }

  if (isLoading && isContinuingSso) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="portal-loading-inline">Preparing sign-in…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35 }}
        className="login-card"
      >
        <div className="login-logo-wrap" aria-hidden="true">
          <img src="/hk-login-logo.png" alt="HK ID logo" className="login-logo-image" />
        </div>
        <div className="login-title-row login-title-center">
          <h2>Secure Portal Login</h2>
          <p className="hint-text">
            {provider?.kinde?.enabled ? 'Continue with your corporate account.' : 'Use your portal credentials.'}
          </p>
        </div>

        {authStateMessage && (
          <div className={`status-banner ${authStateMessage.tone}`}>
            <strong>{authStateMessage.title}</strong>
            <span>{authStateMessage.body}</span>
            {authReason && <small>{authReason}</small>}
          </div>
        )}

        {isLoading ? (
          <div className="portal-loading-inline">Loading authentication...</div>
        ) : provider?.kinde?.enabled ? (
          <form
            className="login-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (isRedirectingToKinde) return;
              onContinueToKinde();
            }}
          >
              <label className="login-field-label" htmlFor="kindeIdentifier">
                Email or username
              </label>
              <input
                id="kindeIdentifier"
                type="text"
                autoComplete="username"
                placeholder="you@company.com / username"
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
              />
              <motion.button
                type="submit"
                className="primary-button"
                whileTap={{ scale: 0.98 }}
                disabled={isRedirectingToKinde}
              >
                {isRedirectingToKinde ? 'Redirecting…' : 'Continue'}
                <ArrowRight size={16} />
              </motion.button>

              {showSocialButtons && (googleConnectionId || appleConnectionId) && (
                <div className="login-divider-row" aria-hidden="true">
                  <span className="login-divider-line" />
                  <span className="login-divider-label">or</span>
                  <span className="login-divider-line" />
                </div>
              )}

              {showSocialButtons && googleConnectionId && (
                <motion.button
                  type="button"
                  className="ghost-button login-social-button"
                  whileTap={{ scale: 0.98 }}
                  onClick={() => startProviderLogin(returnUrl, { connectionId: googleConnectionId, prompt: 'login', maxAge: 0 })}
                >
                  Continue with Google
                  <span className="login-social-mark">G</span>
                </motion.button>
              )}

              {showSocialButtons && appleConnectionId && (
                <motion.button
                  type="button"
                  className="ghost-button login-social-button"
                  whileTap={{ scale: 0.98 }}
                  onClick={() => startProviderLogin(returnUrl, { connectionId: appleConnectionId, prompt: 'login', maxAge: 0 })}
                >
                  Continue with Apple
                  <Apple size={16} />
                </motion.button>
              )}
          </form>
        ) : (
          <form onSubmit={onSubmit} className="login-form">
            <label htmlFor="email">Email</label>
            <input id="email" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required />

            <label htmlFor="password">Password</label>
            <div className="input-with-icon">
              <Lock size={16} />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <motion.button type="submit" className="primary-button" disabled={isSubmitting} whileTap={{ scale: 0.98 }}>
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </motion.button>
          </form>
        )}

        {error && <p className="error-text">{error}</p>}
      </motion.div>
    </div>
  );
}
